import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Sparkles, Pencil, Trash2, FileText, BarChart3, ImageIcon, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { can } from '@/lib/permissions';
import { campaignFlag, isLaunched } from '@/lib/campaignStatus';
import { channelLabel } from '@/lib/campaignChannel';
import { useApiErrorToast } from '@/hooks/useApiErrorToast';

// The campaigns half of the Campaigns page.
//
// This is NOT filtered to WhatsApp. It lists every campaign and renders each one's own `type`;
// the tab it sits behind is labelled WhatsApp because that is the only channel that ships, not
// because the list is narrowed. When SMS and Email arrive, that is when this needs a channel.

interface Campaign {
  id: string;
  name: string;
  description?: string | null;
  type: string;
  status: string;
  createdAt: string;
  // The list endpoint includes only the media's id and name — the campaigns list shows WHICH
  // creative a campaign uses, and pulling every generatedContent blob for a page of cards would be
  // a large payload nothing here reads.
  media?: { id: string; name: string } | null;
}

type FilterKey = 'all' | 'saved' | 'live';

function flagChipClass(flag: 'live' | 'saved') {
  return flag === 'live'
    ? 'bg-green-500/20 text-green-500'
    : 'bg-yellow-500/20 text-yellow-500';
}

interface CampaignsListPanelProps {
  // Opens the setup stepper at /campaigns/new, which is behind SubscriptionGate.
  onCreate: () => void;
  // The page owns the single `useSubscriptionGuard` (and the one `nudge` it renders), so the
  // guard arrives as a prop rather than being called a second time here.
  guard: (fn: () => void) => () => void;
}

export default function CampaignsListPanel({ onCreate, guard }: CampaignsListPanelProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { workspaceId, permissions } = useWorkspace();
  const canDelete = can(permissions, 'campaign:delete');
  const canCreate = can(permissions, 'campaign:create');
  const showError = useApiErrorToast();

  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FilterKey>('all');
  const [deleteTarget, setDeleteTarget] = useState<Campaign | null>(null);
  const [deleting, setDeleting] = useState(false);
  // One at a time: a second click while the first copy is being made would make a second copy.
  const [duplicatingId, setDuplicatingId] = useState<string | null>(null);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const data = await apiFetch<{ campaigns: Campaign[] }>(`/workspaces/${workspaceId}/campaigns`);
      setCampaigns(data.campaigns || []);
    } catch (error) {
      showError(error, 'Failed to load campaigns.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId]);

  const filtered = useMemo(
    () => (filter === 'all' ? campaigns : campaigns.filter((c) => campaignFlag(c.status) === filter)),
    [campaigns, filter],
  );

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await apiFetch(`/workspaces/${workspaceId}/campaigns/${deleteTarget.id}`, { method: 'DELETE' });
      toast.success('Campaign deleted');
      setDeleteTarget(null);
      await fetchCampaigns();
    } catch (error) {
      showError(error, 'Failed to delete campaign.');
    } finally {
      setDeleting(false);
    }
  };

  // The copy carries the details, template and media but not the contacts, so it opens on the
  // contacts step. A copy missing something earlier (an original that never had media) is sent back
  // to that step by CampaignDetail's own `canOpenFromUrl` check.
  const handleDuplicate = async (campaign: Campaign) => {
    if (duplicatingId) return;
    setDuplicatingId(campaign.id);
    try {
      const { campaign: clone } = await apiFetch<{ campaign: { id: string } }>(
        `/workspaces/${workspaceId}/campaigns/${campaign.id}/duplicate`,
        { method: 'POST' },
      );
      toast.success('Campaign duplicated');
      navigate(`/w/${workspaceId}/campaigns/${clone.id}?step=audience`);
    } catch (error) {
      showError(error, 'Failed to duplicate this campaign.');
      setDuplicatingId(null);
    }
  };

  const filters: { key: FilterKey; label: string }[] = [
    { key: 'all', label: t('campaigns.filter_all', 'All') },
    { key: 'saved', label: t('campaigns.filter_saved', 'Saved for later') },
    { key: 'live', label: t('campaigns.filter_live', 'Live') },
  ];

  return (
    <>
      {/* A dropdown rather than a second row of tabs: the channel tabs above are the page's
          navigation, and stacking a second TabsList under them reads as two competing levels of
          the same control. */}
      <div className="mb-6 flex flex-wrap items-center gap-2">
        <span className="text-sm text-muted-foreground">{t('campaigns.filter_label', 'Status')}</span>
        <Select value={filter} onValueChange={(v) => setFilter(v as FilterKey)}>
          <SelectTrigger className="w-[180px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {filters.map((f) => (
              <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading campaigns…
        </div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 flex flex-col items-center justify-center text-center text-muted-foreground">
          <div className="bg-muted/30 p-6 rounded-full mb-4">
            <FileText className="h-10 w-10 opacity-50" />
          </div>
          <h3 className="text-lg font-medium mb-2 text-foreground">
            {filter === 'all' ? 'No campaigns yet' : `No ${filters.find((f) => f.key === filter)?.label.toLowerCase()} campaigns`}
          </h3>
          {/* A button-less exhortation to create is worse than no exhortation, so the copy
              changes rather than leaving a call to action nobody here can act on. */}
          <p className="max-w-xs mb-4">
            {canCreate
              ? 'Create your first campaign and it will show up here to resume, preview or launch.'
              : 'Campaigns your team creates will show up here to preview and follow.'}
          </p>
          {canCreate && (
            <Button onClick={onCreate}>
              <Sparkles className="mr-2 h-4 w-4" /> {t('campaigns.new', 'Create new campaign')}
            </Button>
          )}
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((c) => {
            const flag = campaignFlag(c.status);
            const launched = isLaunched(c.status);
            return (
              <Card key={c.id} className="p-5 flex flex-col gap-3">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-semibold leading-tight line-clamp-2">{c.name}</h3>
                  <Badge className={flagChipClass(flag)}>
                    {flag === 'live' ? t('campaigns.flag_live', 'Live') : t('campaigns.flag_saved', 'Saved for later')}
                  </Badge>
                </div>
                {c.description && (
                  <p className="-mt-1 text-sm text-muted-foreground line-clamp-2" dir="auto">{c.description}</p>
                )}
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span>{channelLabel(c.type)}</span>
                  <span>·</span>
                  <span className="capitalize">{c.status}</span>
                  <span>·</span>
                  <span>{c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''}</span>
                </div>
                <p className="flex min-h-[3rem] items-start gap-2 text-sm text-muted-foreground">
                  <ImageIcon className="mt-0.5 h-4 w-4 shrink-0 opacity-60" />
                  <span className="line-clamp-2">
                    {c.media?.name || <span className="italic">No media chosen yet</span>}
                  </span>
                </p>
                <div className="flex flex-wrap gap-2 pt-1 mt-auto">
                  <Button size="sm" onClick={() => navigate(`/w/${workspaceId}/campaigns/${c.id}`)}>
                    {launched
                      ? <><BarChart3 className="mr-1.5 h-3.5 w-3.5" /> Results</>
                      : <><Pencil className="mr-1.5 h-3.5 w-3.5" /> Set up</>}
                  </Button>
                  {/* Guarded here, so an unsubscribed click shows the nudge rather than a spinner
                      and a refusal: the endpoint is behind requireActiveSubscription. */}
                  {canCreate && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={guard(() => handleDuplicate(c))}
                      disabled={duplicatingId !== null}
                    >
                      {duplicatingId === c.id
                        ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> Duplicating…</>
                        : <><Copy className="mr-1.5 h-3.5 w-3.5" /> Duplicate</>}
                    </Button>
                  )}
                  {canDelete && (
                    <Button size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setDeleteTarget(c)}>
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              "{deleteTarget?.name}" will be removed from your campaigns. This can be undone by support if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={deleting} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {deleting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Deleting…</> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
